import { Component, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { OidcClientService } from '@upmc-healthplan-npm/oidc-client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { selectShouldDisableUserMenu } from '../../state/core.selectors';
import { Router } from '@angular/router';

export interface ReferencesList {
  target: string;
  url: string;
  id: string;
  text: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  store = inject(Store);
  router = inject(Router);
  auth = inject(OidcClientService);

  optionsClicked = false;
  referencesOpened = false;
  userName: string;
  shouldDisableUserMenu = false;

  @ViewChild('references') references;
  @ViewChild('userOptions') userOptions;

  shouldDisableUserMenu$ = this.store.select(selectShouldDisableUserMenu).pipe(
    takeUntilDestroyed(),
    tap(res => {
      this.shouldDisableUserMenu = res;
    })
  );


  referencesList: ReferencesList[] = [
    { 
      target: '_blank',
      url: 'https://upmc.widen.net/s/tmw8bmtqsd/23pv4144200-provider-online-enhancement-rollout---faq-document',
      id: 'frequentlyAskedQuestions',
      text: 'Frequently Asked Questions (FAQ)'
    },
    { 
      target: '_blank',
      url: 'https://upmc.widen.net/s/xjhkcmdksj/24pv4385845-pol-member-look-up---reference-guide_fnl',
      id: 'memberEligibilityQuickReference',
      text: 'Member Look Up Quick Reference'
    },
    {
      target: '_blank',
      url: 'https://upmc.widen.net/s/qgnh5q9mx7/24pv4352659-quick-reference-guide-for-security-management_fnl',
      id:'securityManagementQuickReference',
      text: 'Security Management Quick Reference'
    },
    {
      target: '_blank',
      url: 'https://upmc.widen.net/s/qcjxm7gzw6/24pv4285350-pol-user-manual---02.26.24',
      id:'userManualReference',
      text: 'User Manual'
    },
    {
      target: '_blank',
      url: 'https://npiregistry.cms.hhs.gov/',
      id:'npiLookup',
      text: 'NPI Lookup'
    },
    {
      target: '_blank',
      url: 'https://www.brainshark.com/1/player/upmchp?pi=zHjzuMT8OzKmjWz0&r3f1=f2c8b6e5e9e4bcaefeb6d0aff2f1f2e7eda5abd0aef0f7e3f5f5ffb6d0aff2b7f4e9ecfe&fb=0',
      id:'tutorialVideos',
      text: 'Tutorial Videos'
    },
    {
      target: '_blank',
      url: 'https://marketingassets.upmchp.com/c/dgey4uym',
      id:'interqualAllTypeOfCareResources',
      text: 'Interqual All Type of Care Resources'
    },
    {
      target: '_blank',
      url: 'https://upmc.widen.net/s/xxxrn8kxgz/21pv2409651-interqual-connect-code-list',
      id:'interqualRadiologyCardiologySpecific',
      text: 'Interqual Radiology/Cardiology specific'
    },
    {
      target: '_blank',
      url: 'https://upmc.widen.net/s/plnrdjctps/provideronline_authorizations_qrg',
      id:'authorizationQuickReferenceGuide',
      text: 'Authorization Quick Reference Guide'
    }
  ];
  
  ngOnInit() {
    this.sortReferencesListById(); //This will sort the reference list in alpabetical order.
    this.setUser();
  }

  sortReferencesListById(): void {
    this.referencesList.sort((a, b) => a.id.localeCompare(b.id));
  }

  @HostListener('document:click', ['$event'])
  clickedOut(event){
    if(!this.references.nativeElement.contains(event.target)){
      this.referencesOpened = false;
    }

    if(!this.userOptions.nativeElement.contains(event.target)){
      this.optionsClicked = false;
    }
  }

  concatAndCapitalizeUserName(user: Oidc.Profile) {
    return `${user?.given_name} ${user?.family_name}`.toUpperCase();
  }

  closeOptionsMenu(): void {
    this.referencesOpened = false;
    this.optionsClicked = false;
  }

  setUser() {
    const user = this.auth.getUser();
    this.userName = this.concatAndCapitalizeUserName(user?.profile);
  }

  openUserMenu(): void {
    if(this.shouldDisableUserMenu) {
      return;
    }

    this.optionsClicked = !this.optionsClicked;
  }

  userMenuKeyDown(event: KeyboardEvent): void {
    event.preventDefault();
    const focusedEl = document.querySelector(':focus');

    if(focusedEl.getAttribute('id') === 'user-options') {
      // focus is currently on the ul, set focus to the first child
      (focusedEl.children[0].children[0] as HTMLElement).focus();
    } else {
      // focus is on a child of the ul, get the next child
      let nextFocus = (focusedEl.parentElement.nextSibling as HTMLElement);

      if(nextFocus) {
        // we're somewhere in the middle of the list, focus the next item in the list
        (nextFocus.children[0] as HTMLElement).focus();
      } else {
        // we're at the end of the list, wrap around to the start
        nextFocus = document.querySelector('#user-options');
        (nextFocus.children[0].children[0] as HTMLElement).focus();
      }
    }
  }

  userMenuKeyUp(event: KeyboardEvent): void {
    event.preventDefault();
    const focusedEl = document.querySelector(':focus');

    if(focusedEl.getAttribute('id') === 'user-options') {
      // focus is currently on the ul, set focus to the last child
      const childCount = focusedEl.children.length;
      (focusedEl.children[childCount - 1].children[0] as HTMLElement).focus();
    } else {
      // focus is on a child of the ul, get the previous child
      let nextFocus = (focusedEl.parentElement.previousSibling as HTMLElement);

      if(nextFocus) {
        // we're somewhere in the middle of the list, focus the previous item in the list
        (nextFocus.children[0] as HTMLElement).focus();
      } else {
        // we're at the beginning of the list, wrap around to the end
        nextFocus = document.querySelector('#user-options');
        const childCount = nextFocus.children.length;
        (nextFocus.children[childCount - 1].children[0] as HTMLElement).focus();
      }
    }
  }

  navigate(path: string): void {
    this.router.navigateByUrl(path);
  }
}
